import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '../../ui/Button';
import CustDateRangePicker from '../../ui/CustDateRangePicker';
import { Formik, Field } from 'formik';
import SelectComponent from '../../ui/CustomSelect';
import CustomTable from '../../ui/CustomTable';
import CustomPagination from '../../ui/CustomPagination';
import { TitleText } from '../../ui/Text';
import Axios from 'axios';
import { GET_STAFF_URL } from '../../api/urls';
import { get } from '../../utils/get';

const Reports: React.FC = () => {
  const [reportType, setReportType] = useState<string>('Current Staff List')
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [department, setDepartment] = useState<string>('');
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dateFilter, setDateFilter] = useState("");
  const [page, setPage] = useState<number>(1);

  const itemsPerPage = 5;

  const columns = {
    'Current Staff List': ['Name', 'Designation'],
    '2024 BSC Export': ['Name', 'Department', 'KPI', 'Target'],
    'Staff Performance YTD': ['Name', 'Performance Metric', 'Score', 'Date'],
  };

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    setReportData([]);
    try {
      const response = await Axios.get(GET_STAFF_URL);
      const staffData = response.data.staff.map((staff: any) => ({
        name: staff.staff_name,
        designation: staff.designation,
      }));
      setReportData(staffData);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError('Failed to load report data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleReportTypeChange = (value: string) => setReportType(value);

  const handleExportCSV = () => {
    const csvRows: string[] = [];
    const headers = columns[reportType].join(',');
    csvRows.push(headers);
    console.log(headers);

    const dataToExport = reportData.map(item => {
      if (reportType === 'Current Staff List') {
        return [item.name, item.designation];
      }
      // Handle other report types similarly
      return [];
    });

    dataToExport.forEach(row => csvRows.push(row.join(',')));

    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'report.csv');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportXLS = () => {
    const xlsRows: string[] = []; // Explicitly define the type as string[]
    const headers = ['Name', 'Designation'];
    xlsRows.push(headers.join('\t'));
  
    reportData.forEach(item => {
        const row = [item.name, item.designation].join('\t');
        xlsRows.push(row);
    });
  
    const xlsString = xlsRows.join('\n');
    const blob = new Blob([xlsString], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'report.xls');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleExportPDF = () => {
    const pdfContent = `
        <html>
            <head>
                <title>Report</title>
                <style>
                    /* Add your styles here */
                </style>
            </head>
            <body>
                <h1>${reportType}</h1>
                <table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Designation</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${reportData
                            .map(
                                (item) => `
                            <tr>
                                <td>${item.name}</td>
                                <td>${item.designation}</td>
                            </tr>
                        `
                            )
                            .join('')}
                    </tbody>
                </table>
            </body>
        </html>
    `;

    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    
    const newWindow = window.open(url, '_blank');

    if (newWindow) {
        newWindow.onload = () => {
            newWindow.print();
            newWindow.onafterprint = () => {
                newWindow.close();
            };
        };
    } else {
        console.error('Failed to open new window for PDF export.');
    }
  };

  // const visibleData = reportData.slice(0, itemsPerPage);

  const paginatedData = reportData.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const formattedData = paginatedData.map((item) => ({
    Name: item.name || 'N/A',
    Designation: item.designation || 'N/A',
  }));
  const totalPages = Math.ceil(reportData.length / itemsPerPage);

  return (
    <Container>
      <ControlsContainer>
        <ControlGroup>
          <TitleText>Report Type:</TitleText>
          <SelectComponent
            value={reportType}
            onChange={handleReportTypeChange}
            options={[
              { value: 'Current Staff List', label: 'Current Staff List' },
              { value: '2024 BSC Export', label: '2024 BSC Export' },
              { value: 'Staff Performance YTD', label: 'Staff Performance YTD' },
            ]}
          />
        </ControlGroup>

        {reportType === '2024 BSC Export' && (
          <ControlGroup>
            <TitleText>Department:</TitleText>
            <SelectComponent
              value={department}
              onChange={(value) => setDepartment(value)}
              options={[
                { value: '', label: 'All Departments' },
                { value: 'HR', label: 'HR' },
                { value: 'Sales', label: 'Sales' },
              ]}
            />
          </ControlGroup>
        )}

        {reportType === 'Staff Performance YTD' && (
          <ControlGroup>
            <Label>Date Range:</Label>
            <DateRangeContainer>
              <Formik initialValues={{}} onSubmit={() => console.log("object")}>
                {({ values }) => {
                  setDateFilter(get(values, "date_range"));
                  return (
                    <Field
                      name="date_range"
                      label="Time Period"
                      component={CustDateRangePicker}
                      backgroundColor="#F5F6FB"
                    />
                  );
                }}
              </Formik>
            </DateRangeContainer>
          </ControlGroup>
        )}

        <Button onClick={fetchReportData} text="Generate Report" />
      </ControlsContainer>

      {loading && <Loader>Loading...</Loader>}
      {error && <Error>{error}</Error>}

      {!loading && !error && reportData.length > 0 ? (
        <>
          <CustomTable columns={columns[reportType]} data={formattedData} />
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
          <ExportButtons>
                {reportType === '2024 BSC Export' && (
                  <Button onClick={handleExportXLS} text="Export Excel" backgroundColor="#4CAF50" />
                )}
                <Button onClick={handleExportCSV} text="Export CSV" backgroundColor="#4CAF50" />
                <Button onClick={handleExportPDF} text="Export PDF" backgroundColor="#F44336" />
              </ExportButtons>
        </>
      ) : (
        !loading && <NoData>No data available. Please generate a report.</NoData>
      )}
    </Container>
  );
};

export default Reports;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 40px;
  background-color: #f8f9fa;
  min-height: 100vh;
`;

const ControlsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 30px;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
`;

const Label = styled.label`
  font-weight: 500;
  color: #4a4a4a;
  margin-bottom: 8px;
`;

const Loader = styled.div`
  font-size: 16px;
  color: #007bff;
  text-align: center;
  margin-top: 20px;
`;

const Error = styled.div`
  font-size: 16px;
  color: #d9534f;
  text-align: center;
  margin-top: 20px;
`;

const NoData = styled.div`
  font-size: 16px;
  color: #6c757d;
  text-align: center;
  margin-top: 20px;
`;

const ExportButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
`;

const DateRangeContainer = styled.div`
  width: 300px;
`;